import {
  Box,
  Text,
  Flex,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import type {
  APIMortgagee,
  APIPersonalHomeApplicationForm,
} from 'types/api.types';
import { useBinderCardColors } from '../../binder_report/hooks/useBinderCardColors';
import { isNonStandardLender } from '../utils/lenderValidation';
import MortgageeDisplay from './MortgageeDisplay';

interface NonStandardLenderCardProps {
  applicationData?: APIPersonalHomeApplicationForm | null;
  remarks?: Remark[];
}

const NonStandardLenderCard = ({
  applicationData,
  remarks = [],
}: NonStandardLenderCardProps) => {
  const colors = useBinderCardColors();

  const dwellings = applicationData?.dwellings || [];
  const insuranceCompany =
    applicationData?.policy_information?.insurance_company || '';

  // Get remarks for mortgagees - keyed by item_key (e.g., "mortgagee-0", "mortgagee-1")
  const mortgageeRemarks = remarks.filter(
    (r) =>
      r.validation_key === 'mortgagee_name' &&
      r.item_key?.startsWith('mortgagee-'),
  );

  // Create a map of mortgagee index to remark (0-based)
  const remarksByMortgageeIndex: Record<number, string> = {};
  mortgageeRemarks.forEach((r) => {
    const match = r.item_key?.match(/mortgagee-(\d+)/);
    if (match && r.text) {
      const mortgageeIndex = parseInt(match[1], 10);
      remarksByMortgageeIndex[mortgageeIndex] = r.text;
    }
  });

  // Track global mortgagee index across all dwellings (0-based)
  let globalMortgageeIndex = 0;

  // Build location data with mortgagees and check for non-standard lenders
  const locationData = dwellings.map((dwelling, index) => {
    const locNr = index + 1;
    const mortgagees = (dwelling.mortgagee || []) as APIMortgagee[];

    const mortgageesWithStatus = mortgagees
      .filter((m) => m.name)
      .map((m) => {
        const remark = remarksByMortgageeIndex[globalMortgageeIndex];
        globalMortgageeIndex++;
        return {
          name: m.name || '',
          address: m.address,
          city: m.city,
          provinceCode: m.province_code,
          postalCode: m.postal_code,
          isNonStandard: isNonStandardLender(m.name || '', insuranceCompany),
          remark,
        };
      });

    return {
      locationNumber: locNr,
      mortgagees: mortgageesWithStatus,
      hasNonStandard: mortgageesWithStatus.some((m) => m.isNonStandard),
    };
  });

  // Filter to only show locations that have mortgagees
  const locationsWithMortgagees = locationData.filter(
    (loc) => loc.mortgagees.length > 0,
  );

  // Determine if any location has non-standard lenders
  const hasAnyNonStandard = locationsWithMortgagees.some(
    (loc) => loc.hasNonStandard,
  );

  return (
    <Box
      borderWidth="1px"
      borderColor={colors.borderColor}
      borderRadius="md"
      bg={colors.bgColor}
      overflow="hidden"
    >
      {/* Header */}
      <Flex
        justify="space-between"
        align="center"
        px={4}
        py={3}
        borderBottomWidth="1px"
        borderColor={colors.borderColor}
      >
        <Text fontSize="lg" fontWeight="bold" color={colors.textColor}>
          Non-standard lender
        </Text>
        <Badge
          colorScheme={hasAnyNonStandard ? 'orange' : 'green'}
          fontSize="sm"
          px={3}
          py={1}
        >
          {hasAnyNonStandard ? 'CAUTION' : 'PASS'}
        </Badge>
      </Flex>

      {/* Table */}
      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead bg={colors.tableHeaderBg}>
            <Tr>
              <Th
                width="150px"
                borderColor={colors.borderColor}
                py={3}
                textTransform="none"
                fontSize="sm"
                fontWeight="bold"
                color={colors.textColor}
              >
                Location
              </Th>
              <Th
                borderColor={colors.borderColor}
                py={3}
                textTransform="none"
                fontSize="sm"
                fontWeight="bold"
                color={colors.textColor}
              >
                Mortgagees/Loss payees
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {locationsWithMortgagees.length > 0 ? (
              locationsWithMortgagees.map((location) => (
                <Tr key={location.locationNumber}>
                  <Td
                    borderColor={colors.borderColor}
                    py={3}
                    textAlign="center"
                    fontWeight="medium"
                    verticalAlign="top"
                  >
                    {location.locationNumber}
                  </Td>
                  <Td borderColor={colors.borderColor} py={3}>
                    {location.mortgagees.map((mortgagee, idx) => (
                      <Box
                        key={idx}
                        mb={idx < location.mortgagees.length - 1 ? 3 : 0}
                        pb={idx < location.mortgagees.length - 1 ? 3 : 0}
                        borderBottomWidth={
                          idx < location.mortgagees.length - 1 ? '1px' : 0
                        }
                        borderColor={colors.borderColor}
                      >
                        <MortgageeDisplay mortgagee={mortgagee} />
                      </Box>
                    ))}
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={2} borderColor={colors.borderColor} py={3}>
                  <Text color={colors.descriptionColor} fontStyle="italic">
                    No mortgagees found
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default NonStandardLenderCard;
